import { useEffect, useRef } from "react"
import { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"

/** Caminhos ("address.zipCode") dos campos que estão com erro no formulário. */
export const getErrorPaths = (errors: FieldErrors, prefix = ""): string[] =>
  Object.entries(errors).flatMap(([key, value]) => {
    if (!value || typeof value !== "object") return []
    const path = prefix ? `${prefix}.${key}` : key
    // Erro de campo tem "type"; sem ele é um objeto aninhado (address, payment...).
    if ("type" in value) return [path]
    return getErrorPaths(value as FieldErrors, path)
  })

/**
 * O react-hook-form guarda a mensagem de erro já traduzida. Ao trocar o idioma,
 * revalida só os campos com erro para a mensagem acompanhar o novo idioma.
 * Roda no efeito (depois do render) para as regras já terem os rótulos no idioma novo.
 */
export default function useRevalidateOnLanguageChange<T extends FieldValues>(
  methods: UseFormReturn<T>
) {
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage
  const previous = useRef(language)
  // Ler "errors" no render inscreve o hook nas mudanças de erro do RHF (formState é um proxy).
  const {
    trigger,
    formState: { errors },
  } = methods

  useEffect(() => {
    if (previous.current === language) return
    previous.current = language

    const paths = getErrorPaths(errors)
    if (paths.length > 0) trigger(paths as any)
  }, [language, trigger, errors])
}
